import { Box, Button, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { paymentWatcher } from '../../../redux/actionCreators/user/currentOrder'
import Service from '../../../services/user/currentOrder'

/*
  Orders this tab has already asked /user/payment to book. The effect below runs
  twice under React's StrictMode (and again on "Check again"); two concurrent
  confirmations both passed the server's "already paid?" check and booked the
  order twice wherever tblPayment lacks its unique index (dev databases).
*/
const confirmed = new Set()

// Where Cashfree and ICICI send the browser back to. The payment result is read from
// the gateway itself (through the server) rather than from the redirect, then the
// booking is confirmed.
function VerifyPayment() {
    const { id } = useParams()
    const [params] = useSearchParams()
    const gateway = params.get('gateway')
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [msg, setMsg] = useState('Verifying your payment...')
    const [retry, setRetry] = useState(false)
    const [attempt, setAttempt] = useState(0)

    useEffect(() => {
        // Saved before leaving for the gateway: the vendor's cart lines this payment is for
        let checkout = {}
        try { checkout = JSON.parse(sessionStorage.getItem('pendingCheckout')) || {} } catch (e) { }

        const verifyIcici = async () => {
            const { data } = await Service.verifyIcici({ order_id: id })

            if (data.status === 'PAID') {
                if (confirmed.has(data.OrderID)) return setMsg('This order is paid!')
                confirmed.add(data.OrderID)
                dispatch(paymentWatcher({
                    nOrderID: data.OrderNID,
                    cOrderID: data.OrderID,
                    nTotalRate: data.paidAmount || data.amount,
                    cPaymentStatus: 'Y',
                    cPaymentDesc: 'Success',
                    cBankTrackingID: data.txnId || '',
                    cReferenceID: data.txnId || '',
                    ...checkout,
                    nCafeteriaId: data.nCafeteriaId || checkout.nCafeteriaId
                }, navigate))
                setMsg('This order is paid!')
            } else if (data.status === 'PENDING') {
                // UPI approvals and slow banks finish after the redirect
                setMsg('Your payment is still being processed. Please check again in a minute.')
                setRetry(true)
            } else {
                setMsg(`Order has not been paid${data.reason ? `: ${data.reason}` : '!'}`)
            }
        }

        const verifyCashfree = async () => {
            const { data } = await Service.verifyCashfree({ order_id: id, nCafeteriaId: checkout.nCafeteriaId, dOrderDate: checkout.dOrderDate })

            if (data.order_status === 'PAID') {
                if (confirmed.has(id)) return setMsg('This order is paid!')
                confirmed.add(id)
                const payload = {
                    nOrderID: data.order_tags?.OrderNID,
                    cOrderID: data.order_tags?.OrderID,
                    nTotalRate: data.order_amount,
                    cPaymentStatus: 'Y',
                    cPaymentDesc: 'Success',
                    cBankTrackingID: data.order_id,
                    cReferenceID: `${data.cf_order_id}`,
                    nUserID: data.customer_details?.customer_id,
                    ...checkout,
                    nCafeteriaId: data.order_tags?.cafeteriaId || checkout.nCafeteriaId
                };
                dispatch(paymentWatcher(payload, navigate))
                setMsg('This order is paid!')
            } else {
                setMsg('Order has not been paid!')
            }
        }

        const verify = async () => {
            setRetry(false)
            setMsg('Verifying your payment...')
            try {
                if (gateway === 'ICICI') await verifyIcici()
                else await verifyCashfree()
            }
            catch (e) {
                setMsg(e.response?.data?.message || 'We could not verify this payment. Please check your order history.')
                if (gateway === 'ICICI' && e.response?.status === 502) setRetry(true)
            }
        }
        verify()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, gateway, attempt])

    return (
        <Box sx={{ textAlign: 'center', mt: '100px', px: 2 }}>
            <Typography variant='t18_600'>{msg}</Typography>
            <Box mt='20px' sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                {retry &&
                    <Button variant="outlined" type='button' onClick={() => setAttempt((n) => n + 1)}>
                        Check again
                    </Button>
                }
                <Button variant="contained" component={Link} to='/user' type='button'>
                    Home
                </Button>
            </Box>
        </Box>
    )
}

export default VerifyPayment
